import axios from "axios";

/**
 * LocationClientProfile summary — shape returned by
 * GET /locations/:locationId/profiles. Trimmed to what the profile picker
 * on the Location detail page needs. The full profile (including the
 * contact roster) is fetched via getProfileById when a rep opens the
 * edit modal.
 */
export interface LocationClientProfileSummary {
  id: string;
  clientId: string | null;
  clientName: string | null;
  /** Optional label ("EMCOR FY27 tranche"). Null in the common case. */
  name: string | null;
  /** The org's point person for this site. */
  primaryClientId: string | null;
  primaryClientName: string | null;
  /** The on-site person (facility manager). Free text, not a user. */
  siteContactName: string | null;
  siteContactEmail: string | null;
  updatedAt: string;
}

/**
 * A person assignable as a contact on a profile.
 *
 * `phone` was added in Session 36 to support the Client Detail Contacts
 * tab's row layout without an N+1 fetch. Older callers that only read
 * name / contactEmail still work — the new field is nullable.
 */
export interface AssignableContact {
  id: string;
  name: string | null;
  contactEmail: string | null;
  phone: string | null;
}

/**
 * Full profile — returned by GET /profiles/:id. Adds notes, the contact
 * roster, phone, and PO convention on top of the summary fields.
 */
export interface LocationClientProfile {
  id: string;
  name: string | null;
  notes: string | null;
  location: { id: string; name?: string | null } | null;
  client: { id: string; name?: string | null } | null;
  primaryClient: AssignableContact | null;
  contacts: AssignableContact[];
  siteContactName: string | null;
  siteContactEmail: string | null;
  siteContactPhone: string | null;
  poConvention: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * List profiles at a Location. The Location detail picker calls this once
 * on mount and hides the chip row when the array has 0 or 1 entries — so
 * single-profile Locations render exactly as they did before Phase B.
 *
 * Bypasses the 404 interceptor so unknown locations return [] instead of
 * triggering the app-wide error page (parity with getMapsAtLocation).
 */
export const getProfilesAtLocation = async (
  locationId: string,
): Promise<LocationClientProfileSummary[]> => {
  try {
    const res: any = await axios.get(
      `/locations/${locationId}/profiles`,
      { excludeInterceptor: true } as any,
    );
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};

/**
 * Fetch a single profile with its full contact roster. Used by the
 * profile edit modal.
 */
export const getProfileById = async (
  id: string,
): Promise<LocationClientProfile | null> => {
  try {
    const res: any = await axios.get(`/profiles/${id}`, {
      excludeInterceptor: true,
    } as any);
    return res?.data ?? null;
  } catch {
    return null;
  }
};

/**
 * The people assignable as contacts on a profile whose client is
 * `clientId` — the org's child Clients, or the client itself when it's
 * an individual customer with no children.
 *
 * The backend scopes this to the requesting company and to the given
 * org, so it structurally cannot return a person from a different client
 * organization.
 */
export const getAssignableContacts = async (
  clientId: string,
): Promise<AssignableContact[]> => {
  try {
    const res: any = await axios.get(`/clients/${clientId}/contacts`, {
      excludeInterceptor: true,
    } as any);
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};

/**
 * Update a profile's whitelisted fields. Backend rejects attempts to
 * change locationId, clientId, or companyId — the tuple is the profile's
 * identity. It also rejects any primaryClientId / contactIds that don't
 * belong to the profile's client organization.
 *
 * `contactIds` is a full replacement of the roster: pass the complete
 * desired set, or [] to clear it. Omit the key to leave it untouched.
 */
export interface UpdateProfileInput {
  name?: string | null;
  notes?: string | null;
  primaryClientId?: string | null;
  contactIds?: string[];
  siteContactName?: string | null;
  siteContactEmail?: string | null;
  siteContactPhone?: string | null;
  poConvention?: string | null;
}

export const updateProfile = async (
  id: string,
  data: UpdateProfileInput,
): Promise<LocationClientProfile | { error: string }> => {
  try {
    const res: any = await axios.patch(`/profiles/${id}`, data);
    return res?.data ?? { error: "Update returned no data." };
  } catch (e: any) {
    return e?.response?.data
      ? { error: e.response.data.message || "Update failed." }
      : { error: "Update failed." };
  }
};

/**
 * §4.8 #3/#6 (Session 43) — distinct region labels already in use
 * across the company's LCPs. Feeds the region picker on the user admin
 * edit form (suggestions, not a fixed enum — region is free-text per
 * the `region` column's doc comment).
 */
export const getDistinctRegions = async (): Promise<string[]> => {
  try {
    const res: any = await axios.get(`/locations/regions`, {
      excludeInterceptor: true,
    } as any);
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};

/**
 * N1 Release 1 - CLIENT PORTAL Locations. One row per location the
 * signed-in client owns, resolved entirely server-side (the client's own
 * root org, parent-aware) and gated on their `portalLocationsEnabled`
 * flag. Read-only, lean: location identity + coords, no pricing or
 * internal work-order state. Feeds both the client Locations list and the
 * client map (plain markers in Release 1).
 *
 * Bypasses the 404/403 interceptor so a client without portal access (or
 * with no locations) gets [] instead of the app-wide error page; the
 * calling page decides how to present "nothing to show".
 */
export interface PortalLocation {
  profileId: string;
  profileName: string | null;
  locationId: string | null;
  name: string | null;
  address: any;
  latitude: number | null;
  longitude: number | null;
  /**
   * KNOWN_ISSUES item 18 — which maintenance plan covers this site, if
   * any, and whether that plan lets the client raise work themselves.
   * Null means the site is not on a plan, which the list renders as
   * "Not on a plan" — the badge the mockup calls the whole UX job here,
   * because it is what stops a client being surprised by a separate
   * invoice. Says nothing about pricing or internal state.
   */
  plan?: {
    id: string;
    name: string | null;
    selfServiceEnabled: boolean;
  } | null;
}

/** A paged answer. `total` is every site the client owns, not the page. */
export interface PortalLocationPage {
  items: PortalLocation[];
  total: number;
  page: number;
  pageSize: number;
  /**
   * The org's self-service-granting plan, independent of the page. The
   * site page uses it to offer "Request work" on a site that is not
   * itself on a plan — the grant belongs to the organisation.
   */
  orgSelfServicePlan?: { id: string; name: string | null } | null;
}

/**
 * One page of the client's sites, searched server-side.
 *
 * PAGED SINCE SESSION 80, because production has a client with 1,811
 * sites. Search has to be server-side for the same reason: filtering the
 * browser's copy only worked while the browser had every row, and a
 * search that silently sees one page is worse than none.
 *
 * `locationId` narrows to a single site — the client site page needs one
 * row's plan and used to pull the whole list to find it.
 */
export const getPortalLocations = async (opts?: {
  page?: number;
  pageSize?: number;
  search?: string | null;
  locationId?: string | null;
}): Promise<PortalLocationPage> => {
  const empty: PortalLocationPage = {
    items: [],
    total: 0,
    page: 1,
    pageSize: opts?.pageSize ?? 20,
    orgSelfServicePlan: null,
  };
  try {
    const res: any = await axios.get(`/portal/locations`, {
      excludeInterceptor: true,
      params: {
        ...(opts?.page ? { page: opts.page } : {}),
        ...(opts?.pageSize ? { pageSize: opts.pageSize } : {}),
        ...(opts?.search ? { search: opts.search } : {}),
        ...(opts?.locationId ? { locationId: opts.locationId } : {}),
      },
    } as any);
    const d = res?.data;
    if (d && Array.isArray(d.items)) return d as PortalLocationPage;
    // OLDER API: a bare array of every site, ignoring page/pageSize and
    // search. Page and filter it here so the UI still behaves and, more
    // importantly, does not LIE — returning the whole array while
    // reporting a page count renders every row under a "page 1 of 12"
    // label. This path matters in local development, where the frontend
    // runs against whatever the sandbox API currently has.
    if (Array.isArray(d)) {
      const q = (opts?.search ?? "").trim().toLowerCase();
      const filtered = !q
        ? d
        : d.filter((r: any) => {
            const hay = [
              r?.name,
              r?.address?.line1,
              r?.address?.city,
              r?.address?.state,
              r?.address?.postalCode,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return hay.includes(q);
          });
      const size = opts?.pageSize ?? 20;
      const p = Math.max(opts?.page ?? 1, 1);
      const oneSite = opts?.locationId
        ? filtered.filter((r: any) => r?.locationId === opts.locationId)
        : filtered;
      return {
        items: oneSite.slice((p - 1) * size, p * size),
        total: oneSite.length,
        page: p,
        pageSize: size,
        orgSelfServicePlan:
          d.find((r: any) => r?.plan?.selfServiceEnabled)?.plan ?? null,
      };
    }
    return empty;
  } catch {
    return empty;
  }
};

/** Coordinates only, every site, for the map. See the endpoint's note. */
export interface PortalMapPoint {
  profileId: string;
  locationId: string | null;
  name: string | null;
  address: any;
  latitude: number | null;
  longitude: number | null;
}

export const getPortalLocationMapPoints = async (): Promise<
  PortalMapPoint[]
> => {
  try {
    const res: any = await axios.get(`/portal/locations/map`, {
      excludeInterceptor: true,
    } as any);
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
};
